<template>
    <div class="tab-content">
        <div class="table-container-compact">
            <table class="table-compact">
                <thead>
                    <tr>
                        <th class="text-center">사용자 ID</th>
                        <th class="text-center">이름</th>
                        <th class="text-center">이메일</th>
                        <th class="text-center">역할</th>
                        <th class="text-center">부서</th>
                        <th class="text-center">등록일</th>
                        <th class="text-center">상태</th>
                        <th class="text-center"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="loadingUsers">
                        <td colspan="8" class="text-center"><i class="fas fa-spinner fa-spin"></i> 로딩 중...</td>
                    </tr>
                    <tr v-else-if="filteredUsers.length === 0">
                        <td colspan="8" class="text-center empty">사용자가 없습니다</td>
                    </tr>
                    <tr
                        v-else
                        v-for="user in paginatedUsers"
                        :key="user.userIdx"
                        :class="{ inactive: user.status === 'INACTIVE' }"
                        style="cursor: pointer"
                        @click="openUserDetail(user)"
                    >
                        <td class="text-center">{{ user.loginId }}</td>
                        <td class="text-center">{{ user.userName }}</td>
                        <td class="text-center">{{ user.email }}</td>
                    <td class="text-center">{{ getUserRoleNames(user) }}</td>
                        <td class="text-center">{{ user.department || "-" }}</td>
                        <td class="text-center">{{ formatDate(user.regDate) }}</td>
                        <td class="text-center">{{ formatStatus(user.status) }}</td>
                        <td class="text-center" @click.stop>
                            <div class="action-buttons text-center">
                                <IconButton
                                    icon="fas fa-edit"
                                    size="small"
                                    title="수정"
                                    @click.stop="openUserModal('edit', user)"
                                />
                                <IconButton
                                    icon="fas fa-user-tag"
                                    size="small"
                                    title="역할 관리"
                                    @click.stop="openRoleAssignModal(user)"
                                />
                                <IconButton
                                    icon="fas fa-building"
                                    size="small"
                                    title="시설 권한"
                                    @click.stop="openFacilityPermissionModal(user)"
                                />
                                <IconButton
                                    icon="fas fa-trash"
                                    size="small"
                                    variant="delete"
                                    title="삭제"
                                    @click.stop="deleteUser(user)"
                                />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <TablePagination
            v-if="filteredUsers.length > 0"
            :total-items="filteredUsers.length"
            :current-page.sync="currentUserPage"
            :page-size.sync="usersPerPage"
            :page-size-options="[10, 25, 50, 100]"
            @update:pageSize="filterUsers"
        />

        <UserFormModal v-model="userModalVisible" :mode="userModalMode" :form="userForm" @save="handleSaveUser" />
        <b-modal v-model="userDetailModalVisible" size="lg" centered scrollable body-class="p-0">
            <template #modal-header="{ close }">
                <div class="elite-header-inner">
                    <span class="elite-title-inner">사용자 상세 정보</span>
                    <b-button variant="link" class="elite-header-close p-0 ml-auto" aria-label="닫기" @click="close()">
                        <i class="fas fa-times"></i>
                    </b-button>
                </div>
            </template>
            <template #modal-footer="{ hide }">
                <CarbonButton variant="secondary" @click="hide()">닫기</CarbonButton>
            </template>

            <div v-if="userDetailLoading" class="elite-loading">
                <i class="fas fa-spinner fa-spin fa-2x"></i>
                <p>사용자 정보를 불러오는 중...</p>
            </div>
            <div v-else-if="userDetailError" class="elite-loading">
                <p>{{ userDetailError }}</p>
            </div>
            <div v-else-if="userDetail" class="elite-detail-wrap">
                <main class="elite-main custom-scrollbar">
                    <div class="elite-sections">
                        <section class="elite-section">
                            <div class="elite-section-head">
                                <div class="elite-section-bar"></div>
                                <h2 class="elite-section-title">기본 정보</h2>
                            </div>
                            <div class="elite-settings-grid">
                                <div class="elite-setting">
                                    <div class="elite-setting-label">로그인 ID</div>
                                    <div class="elite-setting-value">{{ userDetail.loginId }}</div>
                                </div>
                                <div class="elite-setting">
                                    <div class="elite-setting-label">이름</div>
                                    <div class="elite-setting-value">{{ userDetail.userName }}</div>
                                </div>
                                <div class="elite-setting">
                                    <div class="elite-setting-label">이메일</div>
                                    <div class="elite-setting-value">{{ userDetail.email || "-" }}</div>
                                </div>
                                <div class="elite-setting">
                                    <div class="elite-setting-label">부서</div>
                                    <div class="elite-setting-value">{{ userDetail.department || "-" }}</div>
                                </div>
                                <div class="elite-setting">
                                    <div class="elite-setting-label">직책</div>
                                    <div class="elite-setting-value">{{ userDetail.position || "-" }}</div>
                                </div>
                                <div class="elite-setting">
                                    <div class="elite-setting-label">상태</div>
                                    <div class="elite-setting-value">
                                        {{ formatStatus(userDetail.status) }}
                                    </div>
                                </div>
                                <div class="elite-setting">
                                    <div class="elite-setting-label">등록일</div>
                                    <div class="elite-setting-value">{{ formatDate(userDetail.regDate) }}</div>
                                </div>
                                <div class="elite-setting">
                                    <div class="elite-setting-label">수정일</div>
                                    <div class="elite-setting-value">{{ formatDate(userDetail.modDate) }}</div>
                                </div>
                                <div class="elite-setting elite-setting-full">
                                    <div class="elite-setting-label">비고</div>
                                    <div class="elite-setting-value">
                                        {{ userDetail.remarks || "-" }}
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section class="elite-section">
                            <div class="elite-section-head">
                                <div class="elite-section-bar"></div>
                                <h2 class="elite-section-title">역할 및 권한</h2>
                            </div>
                            <div v-if="(userDetail.userRoles || []).length === 0" class="text-muted small">
                                할당된 역할이 없습니다.
                            </div>
                            <div
                                v-else
                                v-for="userRole in userDetail.userRoles"
                                :key="userRole.userRoleIdx"
                                class="elite-role-card"
                            >
                                <div class="elite-role-header">
                                    <div class="elite-role-name">
                                        {{ userRole.role?.roleName || userRole.role?.roleCode || "역할" }}
                                    </div>
                                    <div class="elite-role-code">
                                        {{ userRole.role?.roleCode }}
                                    </div>
                                </div>
                                <div class="elite-role-body" v-if="userRole.role?.permissions">
                                    <div
                                        v-for="group in buildPermissionGroups(userRole.role)"
                                        :key="group.key"
                                        class="elite-perm-row"
                                    >
                                        <div class="elite-perm-label">
                                            {{ group.label }}
                                        </div>
                                        <div class="elite-perm-tags">
                                            <span v-for="perm in group.actions" :key="perm" class="tag-small">
                                                {{ getPermissionActionLabel(perm) }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </main>
            </div>
        </b-modal>
        <RoleAssignModal
            v-model="roleAssignModalVisible"
            :user="selectedUser"
            :roles="roles"
            @toggle-role="handleToggleUserRole"
        />
        <UserFacilityPermissionModal
            v-model="facilityPermissionModalVisible"
            :user="selectedUser"
            @updated="
                loadUsers({ preservePage: true });
                $emit('refresh');
            "
        />
    </div>
</template>

<script>
import carbonApi from "@/submodules/carbon-plugin/api";
import CarbonCodes from "../../../codes";
import { RESOURCE_META } from "../../../utils/permissionUtils";
import IconButton from "../../common/IconButton.vue";
import TablePagination from "../../common/TablePagination.vue";
import { CarbonButton } from "../../common";
import UserFormModal from "../modals/UserFormModal.vue";
import RoleAssignModal from "../modals/RoleAssignModal.vue";
import UserFacilityPermissionModal from "../modals/UserFacilityPermissionModal.vue";
import accessControlMixin from "../mixins/accessControlMixin";

export default {
    name: "UserManagementTab",
    components: {
        IconButton,
        TablePagination,
        CarbonButton,
        UserFormModal,
        RoleAssignModal,
        UserFacilityPermissionModal,
    },
    mixins: [accessControlMixin],
    props: {
        roles: { type: Array, default: () => [] },
        filters: {
            type: Object,
            default: () => ({
                searchKeyword: "",
                filterRole: "",
                filterStatus: "",
                filterDepartment: "",
            }),
        },
    },
    data() {
        return {
            users: [],
            loadingUsers: false,
            currentUserPage: 1,
            usersPerPage: 10,

            userModalVisible: false,
            userModalMode: "create",
            userForm: {
                loginId: "",
                userName: "",
                email: "",
                department: "",
                status: "ACTIVE",
            },

            roleAssignModalVisible: false,
            facilityPermissionModalVisible: false,
            selectedUser: null,

            userDetailModalVisible: false,
            userDetailLoading: false,
            userDetailError: "",
            userDetail: null,
        };
    },
    computed: {
        statusFilterOptions() {
            return CarbonCodes.status.getList();
        },
        filteredUsers() {
            let filtered = [...this.users];
            const f = this.filters || {};

            if (f.filterRole) {
                filtered = filtered.filter((user) =>
                    user.roles?.some((role) => role.roleIdx === parseInt(f.filterRole)),
                );
            }

            if (f.searchKeyword) {
                const keyword = (f.searchKeyword || "").toLowerCase();
                filtered = filtered.filter(
                    (user) =>
                        user.loginId?.toLowerCase().includes(keyword) ||
                        user.userName?.toLowerCase().includes(keyword) ||
                        user.email?.toLowerCase().includes(keyword),
                );
            }

            return filtered;
        },
        paginatedUsers() {
            const start = (this.currentUserPage - 1) * this.usersPerPage;
            const end = start + this.usersPerPage;
            return this.filteredUsers.slice(start, end);
        },
    },
    watch: {
        "filters.filterStatus"() {
            this.currentUserPage = 1;
            this.loadUsers();
        },
        "filters.filterDepartment"() {
            this.currentUserPage = 1;
            this.loadUsers();
        },
    },
    mounted() {
        this.loadUsers();
    },
    methods: {
        getUserRoleNames(user) {
            const roles = user?.roles || [];
            const names = roles
                .map((r) => r?.roleName)
                .filter((n) => n != null && String(n).trim().length > 0);
            return names.length ? names.join(", ") : "-";
        },

        async loadUsers(options = {}) {
            const { preservePage = false } = options;
            this.loadingUsers = true;
            try {
                const params = {};
                const f = this.filters || {};
                if (f.filterStatus) params.status = f.filterStatus;
                if (f.filterDepartment?.trim()) params.department = f.filterDepartment.trim();
                const { data } = await carbonApi.accessControl.getUsers(params);
                if (data?.success) {
                    const users = data.data?.users || [];
                    this.users = users.map((user) => {
                        const roles =
                            Array.isArray(user.userRoles) && user.userRoles.length > 0
                                ? user.userRoles
                                      .map(({ role }) => role)
                                      .filter(Boolean)
                                      .map((role) => ({
                                          roleIdx: role.roleIdx,
                                          roleCode: role.roleCode,
                                          roleName: role.roleName,
                                      }))
                                : [];

                        return {
                            userIdx: user.userIdx,
                            loginId: user.loginId,
                            userName: user.userName,
                            email: user.email,
                            department: user.department,
                            status: user.status,
                            regDate: user.regDate,
                            roles,
                        };
                    });
                    if (!preservePage) this.currentUserPage = 1;
                }
            } catch (error) {
                console.error("사용자 목록 로드 실패:", error);
            } finally {
                this.loadingUsers = false;
            }
        },

        filterUsers() {
            this.currentUserPage = 1;
        },

        buildPermissionGroups(role) {
            const permissions = role?.permissions || {};
            const entries = Object.keys(permissions).map((key) => {
                const meta = RESOURCE_META[key] || {};
                return {
                    key,
                    label: meta.label || key,
                    sortOrder: meta.sortOrder != null ? meta.sortOrder : 999,
                    actions: permissions[key] || [],
                };
            });
            return entries.sort((a, b) => a.sortOrder - b.sortOrder);
        },

        getPermissionActionLabel(code) {
            return CarbonCodes.permissionAction.getLabel(code);
        },

        async openUserDetail(user) {
            if (!user || !user.loginId) return;
            this.userDetailModalVisible = true;
            this.userDetailLoading = true;
            this.userDetailError = "";
            this.userDetail = null;
            try {
                const { data } = await carbonApi.accessControl.getUser(user.loginId);
                if (data?.success) {
                    this.userDetail = data.data || null;
                } else {
                    this.userDetailError = data?.message || "사용자 정보를 불러오지 못했습니다.";
                }
            } catch (error) {
                console.error("사용자 상세 조회 실패:", error);
                this.userDetailError = this.getErrorMessage(error, "사용자 정보를 불러오지 못했습니다.");
            } finally {
                this.userDetailLoading = false;
            }
        },

        openUserModal(mode, user = null) {
            this.userModalMode = mode;
            if (mode === "create") {
                this.userForm = {
                    loginId: "",
                    userName: "",
                    email: "",
                    department: "",
                    status: "ACTIVE",
                };
            } else {
                this.userForm = {
                    userIdx: user.userIdx,
                    loginId: user.loginId,
                    userName: user.userName,
                    email: user.email,
                    department: user.department,
                    status: user.status,
                };
            }
            this.userModalVisible = true;
        },

        async handleSaveUser({ payload, mode, form }) {
            try {
                if (mode === "create") {
                    const { data } = await carbonApi.accessControl.createUser(payload);
                    if (data?.success) {
                        this.toastSuccess(data.message || "사용자가 추가되었습니다.");
                    }
                } else {
                    const { data } = await carbonApi.accessControl.updateUser(form.loginId, payload);
                    if (data?.success) {
                        this.toastSuccess(data.message || "사용자 정보가 수정되었습니다.");
                    }
                }
                this.userModalVisible = false;
                await this.loadUsers({ preservePage: mode === "edit" });
                this.$emit("refresh");
            } catch (error) {
                console.error("사용자 저장 실패:", error);
                this.toastError(this.getErrorMessage(error, "사용자 저장에 실패했습니다."));
            }
        },

        async deleteUser(user) {
            const confirmed = await this.$bvModal.msgBoxConfirm(`사용자 "${user.userName}"를 삭제하시겠습니까?`, {
                title: "사용자 삭제",
                okTitle: "삭제",
                cancelTitle: "취소",
                okVariant: "danger",
            });

            if (confirmed) {
                try {
                    const { data } = await carbonApi.accessControl.deleteUser(user.loginId);
                    if (data?.success) {
                        this.toastSuccess(data.message || "사용자가 비활성화되었습니다.");
                        await this.loadUsers({ preservePage: true });
                        this.$emit("refresh");
                    }
                } catch (error) {
                    console.error("사용자 삭제 실패:", error);
                    this.toastError(this.getErrorMessage(error, "사용자 삭제에 실패했습니다."));
                }
            }
        },

        openRoleAssignModal(user) {
            this.selectedUser = user;
            this.roleAssignModalVisible = true;
        },

        openFacilityPermissionModal(user) {
            this.selectedUser = user;
            this.facilityPermissionModalVisible = true;
        },

        async handleToggleUserRole({ roleIdx, checked }) {
            const user = this.selectedUser;
            if (!user) return;
            try {
                if (checked) {
                    const { data } = await carbonApi.accessControl.assignUserRole(user.loginId, roleIdx);
                    if (data?.success) {
                        this.toastSuccess(data.message || "역할이 할당되었습니다.");
                        await this.loadUsers({ preservePage: true });
                        const updated = this.users.find((u) => u.loginId === user.loginId);
                        if (updated) this.selectedUser = updated;
                        this.$emit("refresh");
                    }
                } else {
                    const { data } = await carbonApi.accessControl.revokeUserRole(user.loginId, roleIdx);
                    if (data?.success) {
                        this.toastSuccess(data.message || "역할이 회수되었습니다.");
                        await this.loadUsers({ preservePage: true });
                        const updated = this.users.find((u) => u.loginId === user.loginId);
                        if (updated) this.selectedUser = updated;
                        this.$emit("refresh");
                    }
                }
            } catch (error) {
                console.error("역할 할당/해제 실패:", error);
                this.toastError(this.getErrorMessage(error, "역할 할당/해제에 실패했습니다."));
            }
        },
    },
};
</script>

<style scoped>
.tab-content {
    padding-top: 8px;
}
.tag-small {
    padding: 2px 6px;
    background: #f0fdf4;
    color: #16a34a;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 500;
    margin-right: 4px;
}
.text-center {
    text-align: center;
}
.text-muted {
    color: #94a3b8;
}
.empty {
    padding: 30px;
    color: #94a3b8;
}

.elite-detail-wrap {
    display: flex;
    flex-direction: column;
    max-height: 92vh;
    min-height: 0;
    background: #fff;
}

.elite-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 160px;
    padding: 24px;
    color: #64748b;
}

.elite-loading p {
    margin: 0;
    font-size: 14px;
}

.elite-header-inner {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
}

.elite-title-inner {
    font-size: 1rem;
    font-weight: 700;
    color: #334155;
    letter-spacing: -0.02em;
}

.elite-badges {
    display: flex;
    align-items: center;
    gap: 6px;
}

.elite-badge {
    padding: 2px 8px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.05em;
    border-radius: 4px;
}

.elite-badge-id {
    background: #f1f5f9;
    color: #64748b;
}

.elite-badge-active {
    background: #dcfce7;
    color: #047857;
}

.elite-badge-inactive {
    background: #fee2e2;
    color: #b91c1c;
}

.elite-header-close {
    color: #94a3b8;
}

.elite-main.custom-scrollbar {
    flex: 1 1 auto;
    overflow-y: auto;
}

.elite-main.custom-scrollbar::-webkit-scrollbar {
    width: 6px;
}

.elite-main.custom-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5f5;
    border-radius: 9999px;
}

.elite-main.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

.elite-sections {
    padding: 16px 26px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.elite-section {
    margin: 0;
}

.elite-section-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
}

.elite-section-bar {
    width: 4px;
    height: 20px;
    background: #10b981;
    border-radius: 9999px;
}

.elite-section-title {
    font-size: 11px;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.12em;
    margin: 0;
}

.elite-settings-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 20px;
}

.elite-setting {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.elite-setting-label {
    font-size: 10px;
    font-weight: 700;
    color: #94a3b8;
}

.elite-setting-value {
    font-size: 13px;
    font-weight: 500;
    color: #334155;
}

.elite-setting-full {
    grid-column: 1 / -1;
}

.elite-role-card {
    background: #f8fafc;
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    padding: 12px 14px;
    margin-top: 8px;
}

.elite-role-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
}

.elite-role-name {
    font-weight: 600;
    color: #111827;
}

.elite-role-code {
    font-size: 12px;
    color: #6b7280;
}

.elite-role-body {
    margin-top: 4px;
}

.elite-perm-row {
    display: flex;
    align-items: flex-start;
    margin-top: 4px;
}

.elite-perm-label {
    flex: 0 0 120px;
    font-size: 12px;
    font-weight: 500;
    color: #4b5563;
}

.elite-perm-tags {
    flex: 1 1 auto;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}
</style>
